import { Box, FormControl, MenuItem, Select } from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useCallback, useMemo, useState } from 'react';
import type { CreditQualityBucket } from '../api/chartDataTypes';
import { perf10kSelectSx } from '../styles/chartReader.styles';
import { LabelTypography, Perf10kSectionTitleTypography } from '../styles/chartReader.typography';

const menuPaperSx = {
    border: '1px solid #E4E4E4',
    borderRadius: '4px',
    boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
};

const MENU_PROPS = { PaperProps: { sx: menuPaperSx } };

type CreditQualitySelectorProps = {
    /** Snapshot buckets from the credit_quality payload. Order is irrelevant —
     *  the selector sorts by `grade` ascending internally. */
    buckets: CreditQualityBucket[];
    /** Prefix for the Credit Grade `<select>` id so multiple snapshot selectors
     *  on a page never collide. */
    idPrefix: string;
    /** Fires when the user picks a grade. Selecting the same grade again still
     *  fires so the parent can re-announce. */
    onSelectedBucketChange: (bucket: CreditQualityBucket) => void;
};

/**
 * Single-dropdown selector for the Credit Quality Breakdown chart. Mirrors
 * SectorSelector — same UX pattern, different field names. Fires the parent's
 * callback on each pick so the shell's live region re-announces the new summary.
 */
export function CreditQualitySelector({
    buckets,
    idPrefix,
    onSelectedBucketChange,
}: CreditQualitySelectorProps) {
    const [selectedGrade, setSelectedGrade] = useState<string>('');

    // Alphabetical ascending by grade so NVDA users can predict where each
    // grade sits in the list.
    const sortedBuckets = useMemo(() => {
        return [...buckets].sort((a, b) => a.grade.localeCompare(b.grade));
    }, [buckets]);

    const handleChange = useCallback(
        (event: SelectChangeEvent<string>) => {
            const nextGrade = event.target.value;
            setSelectedGrade(nextGrade);
            const match = sortedBuckets.find((b) => b.grade === nextGrade);
            if (match) onSelectedBucketChange(match);
        },
        [sortedBuckets, onSelectedBucketChange],
    );

    const gradeId = `${idPrefix}-grade-select`;

    return (
        <Box>
            <Perf10kSectionTitleTypography>Read a specific credit rating</Perf10kSectionTitleTypography>

            <Box sx={{ mt: 1.5 }}>
                <label htmlFor={gradeId}>
                    <LabelTypography sx={{ mb: 0.5 }}>Data Series</LabelTypography>
                </label>
                <FormControl fullWidth size='small' sx={perf10kSelectSx}>
                    <Select<string>
                        id={gradeId}
                        value={selectedGrade}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(v) => (v ? v : 'Select data series')}
                        inputProps={{ 'aria-label': selectedGrade === '' ? 'Select data series' : 'Data series' }}
                        MenuProps={MENU_PROPS}>
                        {sortedBuckets.map((b) => (
                            <MenuItem key={b.grade} value={b.grade}>
                                {b.grade}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Box>
        </Box>
    );
}
